/**
 * palette.js — the carnival art direction, in one place.
 *
 * Warm sunset carnival: cream canvas, awning red and teal stripes, mustard
 * gold hardware, deep plum for signage and shadow accents, honest wood.
 * Everything in `src/assets3d/` pulls its colours from here so twelve booths
 * built by twelve different hands still read as one fairground.
 *
 * Numbers are Three.js hex ints; the `CSS` mirror holds the same colours as
 * strings for canvas painting.
 *
 * @module assets3d/palette
 */

/** Core palette. */
export const ART = Object.freeze({
  // Canvas / fabric
  cream: 0xf6eedf,
  creamWarm: 0xece0c8,
  creamShade: 0xd8c9ab,

  // Awning red
  red: 0xd9483b,
  redDeep: 0xa8342a,
  redLight: 0xe8695c,

  // Teal
  teal: 0x2e8c8c,
  tealDeep: 0x1e6363,
  tealLight: 0x4faaa8,

  // Mustard gold — hardware, bulbs, trim
  gold: 0xe9b44c,
  goldDeep: 0xc08f2c,
  goldLight: 0xf6d189,

  // Deep plum — signage grounds, shadow accents
  plum: 0x5a3a5e,
  plumDeep: 0x3a2440,
  plumLight: 0x7c5580,

  // Timber
  wood: 0x8b5e3c,
  woodDark: 0x64432a,
  woodLight: 0xac7c52,

  // Ground cover
  grass: 0x7a8c4f,
  grassDark: 0x5d6c3c,

  // Neutrals
  metal: 0xb4b9c0,
  metalDark: 0x7c828c,
  iron: 0x4a4750,
  ink: 0x2a2028,
  white: 0xfbf7f0,

  // Emissive bulb tones
  bulbWarm: 0xffd9a0,
  bulbHot: 0xfff0cf,
});

/** The same palette as CSS strings, for canvas painting. */
export const CSS = Object.freeze(
  Object.fromEntries(
    Object.entries(ART).map(([k, v]) => [k, `#${v.toString(16).padStart(6, '0')}`]),
  ),
);

/**
 * Convert a hex int to a CSS string.
 * @param {number|string} c
 * @returns {string}
 */
export function css(c) {
  if (typeof c === 'string') return c;
  return `#${(c >>> 0).toString(16).padStart(6, '0').slice(-6)}`;
}

/**
 * Mix two hex colours.
 * @param {number} a
 * @param {number} b
 * @param {number} t 0 = a, 1 = b.
 * @returns {number}
 */
export function mix(a, b, t) {
  const ar = (a >> 16) & 255, ag = (a >> 8) & 255, ab = a & 255;
  const br = (b >> 16) & 255, bg = (b >> 8) & 255, bb = b & 255;
  return (
    (Math.round(ar + (br - ar) * t) << 16) |
    (Math.round(ag + (bg - ag) * t) << 8) |
    Math.round(ab + (bb - ab) * t)
  );
}

/** Darken toward black. @param {number} c @param {number} t @returns {number} */
export const shade = (c, t) => mix(c, 0x000000, t);
/** Lighten toward white. @param {number} c @param {number} t @returns {number} */
export const tint = (c, t) => mix(c, 0xffffff, t);

// ─────────────────────────────────────────────────────────────────────────────
// Booth families
//
// The twelve stalls arrive carrying their own GDD colours, which range across
// the spectrum and would make the midway look like a paint chart. Each one is
// snapped to the nearest of four carnival families instead, so the row reads as
// a single fairground while each booth still keeps its own identity.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * @typedef {Object} BoothFamily
 * @property {string} key
 * @property {number} canopy   Primary awning stripe.
 * @property {number} canopyB  Alternating awning stripe (always a cream).
 * @property {number} trim     Painted woodwork.
 * @property {number} valance  Scalloped skirt under the canopy.
 * @property {number} sign     Sign ground.
 */

/** @type {Record<string, BoothFamily>} */
export const BOOTH_FAMILIES = Object.freeze({
  red: {
    key: 'red',
    canopy: ART.red, canopyB: ART.cream, trim: ART.cream,
    valance: ART.gold, sign: ART.plumDeep,
  },
  teal: {
    key: 'teal',
    canopy: ART.teal, canopyB: ART.cream, trim: ART.cream,
    valance: ART.gold, sign: ART.plumDeep,
  },
  gold: {
    key: 'gold',
    canopy: ART.gold, canopyB: ART.cream, trim: ART.plum,
    valance: ART.red, sign: ART.plumDeep,
  },
  plum: {
    key: 'plum',
    canopy: ART.plum, canopyB: ART.cream, trim: ART.gold,
    valance: ART.teal, sign: ART.plumDeep,
  },
});

const FAMILY_ORDER = ['red', 'teal', 'gold', 'plum'];

/** Hue in degrees, 0–360. @param {number} hex @returns {number} */
function hueOf(hex) {
  const r = ((hex >> 16) & 255) / 255;
  const g = ((hex >> 8) & 255) / 255;
  const b = (hex & 255) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const d = max - min;
  if (d < 1e-4) return 0;
  let h;
  if (max === r) h = ((g - b) / d) % 6;
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return (h * 60 + 360) % 360;
}

/**
 * Snap an arbitrary stall colour onto the carnival palette.
 * Warm reds and oranges become the red family, cyans and greens teal, yellows
 * gold, and anything violet plum.
 *
 * @param {number|string|undefined} color A GDD stall colour, hex int or '#rrggbb'.
 * @returns {BoothFamily}
 */
export function boothFamily(color) {
  if (color === undefined || color === null) return BOOTH_FAMILIES.red;
  const hex = typeof color === 'string'
    ? parseInt(color.replace('#', ''), 16)
    : color >>> 0;
  if (Number.isNaN(hex)) return BOOTH_FAMILIES.red;

  const h = hueOf(hex);
  if (h < 20 || h >= 330) return BOOTH_FAMILIES.red;
  if (h < 45) return BOOTH_FAMILIES.red;      // oranges read as awning red
  if (h < 70) return BOOTH_FAMILIES.gold;
  if (h < 200) return BOOTH_FAMILIES.teal;    // greens + cyans
  if (h < 260) return BOOTH_FAMILIES.teal;    // blues
  return BOOTH_FAMILIES.plum;                 // violets + magentas
}

/** Family by index, for anything that just needs variety. */
export const familyAt = (i) => BOOTH_FAMILIES[FAMILY_ORDER[i % FAMILY_ORDER.length]];

// ─────────────────────────────────────────────────────────────────────────────
// Seeded randomness
//
// Asset generation must produce the same model every run — screenshots are
// diffed and peer tests assert positions. `core/testmode.js` owns the GAMEPLAY
// RNG; this one is local to asset construction and never shares its stream.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Mulberry32. Small, fast, and deterministic from a fixed seed.
 * @param {number} seed
 * @returns {() => number} Generator in [0, 1).
 */
export function mulberry32(seed) {
  let a = seed >>> 0;
  return function next() {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** The asset-construction seed. Fixed forever — changing it reshuffles every model. */
export const ASSET_SEED = 0xca24172a;

/**
 * A fresh generator for one asset kind. Seeding per-kind means adding a new
 * prop cannot shift the random details of an existing one.
 * @param {string} key
 * @returns {() => number}
 */
export function rngFor(key) {
  let h = ASSET_SEED;
  for (let i = 0; i < key.length; i++) h = Math.imul(h ^ key.charCodeAt(i), 0x01000193) >>> 0;
  return mulberry32(h);
}
